// ============================================
// Risk Radar — "Summarise it for me", Exposure layer
//
// The summary is of ONE MOMENT, never of the live page: the button freezes
// the view on screen, and everything below is built from that frozen view.
//
//   • Every figure is computed here, by the page's own formatters. The AI is
//     given these facts as text and only words them; the server rejects any
//     AI text that carries a number not found in the facts, and the page
//     then shows the summary written here instead (fallback).
//   • The panel says which moment it is for, how old it is, and what has
//     moved on the live page since (driftOf) — so a stale summary is never
//     read as the current picture.
//
// Pure: no React, no fetching.
// ============================================

import {
  BAND_BY_ID,
  fmtLots,
  fmtMovePct,
  fmtPct,
  fmtUsd,
  fmtUtcDateTime,
  sideOf,
  type Band,
  type RadarSymbol,
  type RadarView,
  type Trend,
} from './model';

export interface SummaryFact { id: string; text: string }
export interface KeyFigure { label: string; value: string; note?: string; band?: Band | null }

export interface SummaryText {
  headline: string;
  points: string[];
  actions: string[];
}

/** What the moment looked like, kept to tell the reader what has moved since. */
export interface Fingerprint {
  bookRisk: number;
  bookBand: Band | null;
  capacityLeft: number | null;
  top: string | null;
  bands: Record<string, Band | null>;
}

export interface FrozenSummary<F = Fingerprint> {
  /** Time of the snapshot summarised, epoch ms. */
  asOf: number;
  /** When the button was pressed, epoch ms. */
  takenAt: number;
  facts: SummaryFact[];
  keyFigures: KeyFigure[];
  /** The summary written in code: shown while the AI works, and if it fails. */
  fallback: SummaryText;
  fingerprint: F;
}

export interface SummaryHelpers {
  /** The drawer's hedge line for an instrument, or null. */
  hedgeIssue: (s: RadarSymbol) => string | null;
  /** The instrument's risk trend over the recorded history, or null. */
  trendOf: (symbol: string) => Trend | null;
  /** The book's potential-loss trend, or null. */
  bookTrend: Trend | null;
}

const TOP_N = 3;

function signedUsd(v: number): string {
  return v > 0 ? `+${fmtUsd(v)}` : fmtUsd(v);
}

function lowerFirst(t: string): string {
  return t.charAt(0).toLowerCase() + t.slice(1);
}

function bandWord(b: Band | null): string {
  return b ? BAND_BY_ID[b].label.toUpperCase() : 'no band';
}

function trendWords(t: Trend | null): string | null {
  if (!t || t.change === null) return null;
  const pct = `${t.change >= 0 ? '+' : '−'}${Math.abs(t.change * 100).toFixed(1)}%`;
  const mins = Math.max(1, Math.round(t.spanMs / 60_000));
  const dir = t.dir === 'building' ? 'building' : t.dir === 'easing' ? 'easing' : 'steady';
  return `${pct} over the last ${mins} min (${dir})`;
}

function capacityWords(view: RadarView, risk: number): string {
  const cap = view.capacity;
  if (cap.state !== 'ok' || cap.remaining === null) return 'no share of capacity: the loss capacity is not known';
  if (cap.remaining <= 0) return 'the loss capacity of the month is already used up';
  const head = cap.remaining - risk;
  const use = `uses ${fmtPct(risk / cap.remaining)} of the loss capacity left`;
  return head >= 0 ? `${use}, ${fmtUsd(head)} headroom` : `${use}, ${fmtUsd(-head)} over`;
}

/**
 * @param book   the snapshot on screen, measured with the platform scope "Both":
 *               the summary is always of the whole book, like the cards
 */
export function freezeExposure(
  book: RadarView,
  asOf: number,
  scopeText: string,
  helpers: SummaryHelpers,
): FrozenSummary {
  const facts: SummaryFact[] = [];
  const add = (id: string, text: string) => facts.push({ id, text });
  const cap = book.capacity;

  // ── Moment and capacity ──
  add('time', `Snapshot summarised: ${fmtUtcDateTime(asOf)}. Valid for that moment only; the live page moves on.`);
  if (cap.state === 'ok' && cap.limit !== null) {
    add('limit', `Monthly loss limit: ${fmtUsd(cap.limit)}. It is set by the broker in Settings → Risk Limits as its risk tolerance for the current month${cap.carriedOver ? ', carried over from last month because none was set' : ''}. It is not calculated by Taiga.`);
    add('taken', cap.lossTaken
      ? `Losses already taken this month (realised, whole book): ${fmtUsd(cap.lossTaken)}.`
      : 'No loss has been taken this month.');
    add('left', `Loss capacity left this month: ${fmtUsd(cap.remaining)}.`);
  } else if (cap.state === 'no_limit') {
    add('limit', 'No monthly loss limit is set in Settings → Risk Limits, so no risk can be put against a capacity.');
  } else {
    add('limit', 'The loss capacity is not known: the limit or the month-to-date P&L has not loaded.');
  }

  // ── The book ──
  const at = book.symbols.filter((s) => (s.risk ?? 0) > 0);
  add('book', `Potential loss of the whole book on a bad day (MT5 and cTrader together): ${fmtUsd(book.bookRisk)}, band ${bandWord(book.bookBand)}, ${capacityWords(book, book.bookRisk)}.`);
  const bc = book.bookBandCounts;
  add('count', `Instruments at risk: ${book.bookAtRisk}. By band: Low ${bc.low}, Warning ${bc.warning}, High ${bc.high}, Critical ${bc.critical}.`);
  const bt = trendWords(helpers.bookTrend);
  if (bt) add('book_trend', `The book's potential loss changed ${bt}.`);
  if (book.unrated + book.unpriced > 0) {
    add('unmeasured', `Instruments that cannot be measured and are left out of the potential loss: ${book.unrated} unrated (too little price history), ${book.unpriced} unpriced (no price, value or P&L).`);
  }

  // ── The instruments that matter most ──
  const top = at.slice(0, TOP_N);
  top.forEach((s, i) => {
    const risk = s.risk as number;
    const parts = [
      `${s.symbol}${i === 0 ? ' (largest risk in the book)' : ''}: could lose ${fmtUsd(risk)} on a bad day, band ${bandWord(s.band)}, ${capacityWords(book, risk)}`,
      book.bookRisk > 0 ? `${fmtPct(risk / book.bookRisk)} of the book's potential loss` : null,
      s.u !== null ? `unrealised P&L now ${signedUsd(s.u)}` : null,
      s.movePct !== null ? `adverse move (a bad day for it) ${fmtMovePct(s.movePct)}` : null,
      `net position ${fmtLots(s.netLots)} lots, ${sideOf(s.netLots)}${s.netUsd !== null ? ` (${fmtUsd(s.netUsd)})` : ''}`,
      `books: B-Book ${fmtLots(s.bLots)} lots, A-Book ${fmtLots(s.aLots)} lots, C-Book ${fmtLots(s.cLots)} lots`,
    ].filter(Boolean);
    add(`sym_${s.symbol}`, `${parts.join('; ')}.`);
    const hedge = helpers.hedgeIssue(s);
    if (hedge) add(`hedge_${s.symbol}`, `${s.symbol} hedge: ${lowerFirst(hedge)}.`);
    const tw = trendWords(helpers.trendOf(s.symbol));
    if (tw) add(`trend_${s.symbol}`, `${s.symbol} risk changed ${tw}.`);
  });
  const rest = at.slice(TOP_N);
  if (rest.length > 0) {
    const restRisk = rest.reduce((m, s) => m + (s.risk as number), 0);
    add('rest', `The other ${rest.length} instrument${rest.length === 1 ? '' : 's'} at risk together: ${fmtUsd(restRisk)}.`);
  }

  // ── What the reader can open ──
  add('scope', `The page was showing the ${scopeText} tab; every risk figure is whole book, because hedges and the C-Book are not split by platform.`);
  add('pages', 'Pages the risk manager can open from Risk Radar for review: Review hedge (Hedging Strategies), Inspect positions (B-Book), Risk Limits (Settings). Nothing is executed from Risk Radar.');

  // ── Key figures, shown above the words ──
  const keyFigures: KeyFigure[] = [
    { label: 'Potential loss, whole book', value: fmtUsd(book.bookRisk), note: capacityWords(book, book.bookRisk), band: book.bookBand },
    {
      label: 'Loss capacity left this month',
      value: cap.remaining !== null ? fmtUsd(cap.remaining) : '—',
      note: cap.limit !== null ? `broker's limit ${fmtUsd(cap.limit)}${cap.lossTaken ? `, less ${fmtUsd(cap.lossTaken)} lost` : ', no loss taken'}` : 'no limit set',
    },
    ...top.map((s) => ({
      label: s.symbol,
      value: fmtUsd(s.risk),
      note: `${bandWord(s.band).toLowerCase()} · ${fmtLots(s.netLots)} lots ${sideOf(s.netLots)}`,
      band: s.band,
    })),
  ];

  // ── The summary written in code ──
  const fallback = fallbackText(book, top, helpers);

  const bands: Record<string, Band | null> = {};
  for (const s of top) bands[s.symbol] = s.band;

  return {
    asOf,
    takenAt: Date.now(),
    facts,
    keyFigures,
    fallback,
    fingerprint: {
      bookRisk: book.bookRisk,
      bookBand: book.bookBand,
      capacityLeft: cap.remaining,
      top: book.bookTop?.symbol ?? null,
      bands,
    },
  };
}

function fallbackText(book: RadarView, top: RadarSymbol[], helpers: SummaryHelpers): SummaryText {
  const cap = book.capacity;
  const headline = cap.state === 'ok' && cap.remaining !== null && cap.remaining > 0
    ? `On a bad day the book could lose ${fmtUsd(book.bookRisk)} of the ${fmtUsd(cap.remaining)} it can still lose this month (${bandWord(book.bookBand)}).`
    : `On a bad day the book could lose ${fmtUsd(book.bookRisk)}.`;

  const points: string[] = [];
  const first = top[0];
  if (first && book.bookRisk > 0) {
    points.push(`${first.symbol} is the largest risk: ${fmtUsd(first.risk)}, ${fmtPct((first.risk as number) / book.bookRisk)} of the total.`);
  }
  for (const s of top) {
    const h = helpers.hedgeIssue(s);
    if (h) { points.push(`${s.symbol}: ${lowerFirst(h)}.`); break; }
  }
  if (book.unrated + book.unpriced > 0) {
    points.push(`${book.unrated + book.unpriced} instrument${book.unrated + book.unpriced === 1 ? ' is' : 's are'} not measured and left out of the total.`);
  }

  const actions: string[] = [];
  const hedged = top.find((s) => helpers.hedgeIssue(s));
  if (hedged) actions.push(`Open Review hedge for ${hedged.symbol} and check why its hedge does not offset the B-Book.`);
  if (first && (first.band === 'high' || first.band === 'critical')) {
    actions.push(`Open Inspect positions for ${first.symbol} to see which client positions drive it.`);
  }
  if (cap.state !== 'ok') actions.push('Set or check the monthly loss limit in Settings → Risk Limits.');

  return { headline, points: points.slice(0, 3), actions: actions.slice(0, 3) };
}

// ── What has moved since ─────────────────────────────────────

export interface Drift {
  /** True when the summary no longer matches the page: a band changed, the largest instrument changed, or the book moved 5% or more. */
  stale: boolean;
  lines: string[];
}

const DRIFT_PCT = 0.05;

/** @param now  the live view measured with the platform scope "Both" (whole book) */
export function driftOf(f: Fingerprint, now: RadarView): Drift {
  const lines: string[] = [];
  let stale = false;

  const d = now.bookRisk - f.bookRisk;
  const rel = f.bookRisk > 0 ? d / f.bookRisk : (now.bookRisk > 0 ? 1 : 0);
  if (Math.abs(d) >= 1) {
    lines.push(`Potential loss ${fmtUsd(f.bookRisk)} → ${fmtUsd(now.bookRisk)} (${d >= 0 ? '+' : '−'}${Math.abs(rel * 100).toFixed(1)}%)`);
  }
  if (Math.abs(rel) >= DRIFT_PCT) stale = true;
  if (now.bookBand !== f.bookBand) {
    lines.push(`Book band ${bandWord(f.bookBand)} → ${bandWord(now.bookBand)}`);
    stale = true;
  }
  if (now.capacity.remaining !== f.capacityLeft) {
    lines.push(`Capacity left ${fmtUsd(f.capacityLeft)} → ${fmtUsd(now.capacity.remaining)}`);
    stale = true;
  }
  const nowTop = now.bookTop?.symbol ?? null;
  if (nowTop !== f.top) {
    lines.push(`Largest risk ${f.top ?? 'none'} → ${nowTop ?? 'none'}`);
    stale = true;
  }
  for (const [sym, was] of Object.entries(f.bands)) {
    const s = now.bySymbol.get(sym) ?? null;
    const is = s?.band ?? null;
    if (is !== was) {
      lines.push(`${sym} ${bandWord(was)} → ${bandWord(is)}`);
      stale = true;
    }
  }
  return { stale, lines };
}
